/**
 * @fileoverview Organization Owner Permission Hook
 * 
 * Simple permission check hook that determines if the current user is the owner
 * of their organization. Used throughout the application to control access to
 * sensitive organization-level settings and administrative functions.
 * 
 * Organization Roles:
 * - owner: Full administrative control, can manage settings, members, billing
 * - admin: Can manage most organization settings except ownership transfer
 * - member: Standard member with limited administrative capabilities
 * 
 * Owner Privileges:
 * - Edit company profile and branding
 * - Manage organization members and roles
 * - Access billing and subscription settings
 * - Transfer organization ownership
 * - Delete organization
 * - Configure payment methods
 * - Manage organization-wide settings
 * 
 * Use Cases:
 * - Protecting organization settings pages
 * - Showing/hiding administrative UI elements
 * - Controlling access to dangerous actions (delete org, transfer ownership)
 * - Displaying owner-only notices and warnings
 * - Restricting payment method management
 * 
 * Security:
 * - Role data comes from authenticated user context
 * - Verified server-side via RLS policies
 * - Changes to roles require re-authentication
 * 
 * @module useIsOrganizationOwner
 * 
 * @example
 * ```tsx
 * function OrganizationSettings() {
 *   const isOwner = useIsOrganizationOwner();
 * 
 *   return (
 *     <div>
 *       <h1>Organization Settings</h1>
 *       
 *       {isOwner ? (
 *         <>
 *           <CompanyProfileEditor />
 *           <MemberManagement />
 *           <BillingSettings />
 *           <DangerZone />
 *         </>
 *       ) : (
 *         <Alert>
 *           Only organization owners can access these settings.
 *           Please contact your organization owner for changes.
 *         </Alert>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Conditionally render owner-only actions
 * function OrganizationHeader() {
 *   const isOwner = useIsOrganizationOwner();
 * 
 *   return (
 *     <header>
 *       <h2>My Organization</h2>
 *       {isOwner && (
 *         <Button variant="ghost" onClick={editOrganization}>
 *           Edit
 *         </Button>
 *       )}
 *     </header>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Protect dangerous actions
 * function DeleteOrganizationButton() {
 *   const isOwner = useIsOrganizationOwner();
 * 
 *   if (!isOwner) return null;
 * 
 *   return (
 *     <Button 
 *       variant="destructive"
 *       onClick={handleDelete}
 *     >
 *       Delete Organization
 *     </Button>
 *   );
 * }
 * ```
 */

import { useAuth } from '@/lib/auth';

/**
 * Hook to check if the current user is their organization's owner
 * 
 * Returns a boolean indicating whether the authenticated user has the 'owner'
 * role within their organization. This is the highest privilege level and
 * grants full administrative control over organization settings, members,
 * billing, and sensitive operations.
 * 
 * The role is verified server-side and cached in the user's auth context.
 * Any changes to user roles require re-authentication to take effect.
 * 
 * @returns True if user is organization owner, false otherwise
 * 
 * @example
 * ```typescript
 * const isOwner = useIsOrganizationOwner();
 * 
 * if (isOwner) {
 *   // Show owner-only UI
 * }
 * ```
 */
export const useIsOrganizationOwner = (): boolean => {
  const { user } = useAuth();
  
  // Check if user has 'owner' role in their organization
  return user?.orgRole === 'owner';
};
